import React, { useState, useRef, useCallback, useEffect } from "react";

/**
 * DonutLabelingImageViewer — Task 1.4
 *
 * Props:
 *   sample: { id, doc_type, split, image_proxy_url }
 *
 * Features:
 *   - +/- buttons and mouse wheel zoom (0.25x – 4x)
 *   - Mouse drag pan
 *   - "리셋" resets scale + offset
 */
export default function DonutLabelingImageViewer({ sample }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null); // { mouseX, mouseY, offsetX, offsetY }
  const containerRef = useRef(null);

  // Reset zoom/pan when sample changes
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [sample?.id]);

  const clampScale = (s) => Math.min(4, Math.max(0.25, s));

  const zoomIn = () => setScale((s) => clampScale(s * 1.2));
  const zoomOut = () => setScale((s) => clampScale(s / 1.2));
  const reset = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  // Mouse wheel zoom
  const onWheel = useCallback((e) => {
    e.preventDefault();
    setScale((s) => clampScale(e.deltaY < 0 ? s * 1.1 : s * 0.9));
  }, []);

  // Attach wheel listener with { passive: false } so preventDefault works
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  // Drag handlers
  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };
  };

  const onMouseMove = useCallback(
    (e) => {
      if (!dragging || !dragStart.current) return;
      const dx = e.clientX - dragStart.current.mouseX;
      const dy = e.clientY - dragStart.current.mouseY;
      setOffset({
        x: dragStart.current.offsetX + dx,
        y: dragStart.current.offsetY + dy,
      });
    },
    [dragging],
  );

  const onMouseUp = useCallback(() => {
    setDragging(false);
    dragStart.current = null;
  }, []);

  // Global mouse up/move so drag works even if cursor leaves container
  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging, onMouseMove, onMouseUp]);

  if (!sample) {
    return (
      <div
        style={{
          border: "1px solid #ddd",
          height: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9ca3af",
          fontSize: 13,
        }}
      >
        샘플 없음
      </div>
    );
  }

  const btnBase = {
    fontSize: 13,
    padding: "3px 10px",
    borderRadius: 4,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
    lineHeight: 1.4,
    color: "#374151",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "85vh" }}>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
          borderBottom: "1px solid #ddd",
          background: "#f9fafb",
          flexShrink: 0,
        }}
      >
        {/* Zoom controls (left) */}
        <button style={btnBase} onClick={zoomIn} title="확대 (+)">
          +
        </button>
        <button style={btnBase} onClick={zoomOut} title="축소 (-)">
          −
        </button>
        <button style={btnBase} onClick={reset} title="원래 크기로">
          리셋
        </button>
        <span
          style={{
            fontSize: 11,
            color: "#9ca3af",
            marginLeft: 2,
            minWidth: 36,
          }}
        >
          {Math.round(scale * 100)}%
        </span>

        {/* Sample meta (right) */}
        <span
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: "#6b7280",
            fontFamily: "monospace",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "60%",
          }}
        >
          {sample.id} · {sample.doc_type} · {sample.split}
        </span>
      </div>

      {/* Image viewport */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          border: "1px solid #ddd",
          borderTop: "none",
          overflow: "hidden",
          position: "relative",
          background: "#f3f4f6",
          cursor: dragging ? "grabbing" : "grab",
          userSelect: "none",
        }}
        onMouseDown={onMouseDown}
      >
        {sample.image_proxy_url ? (
          <img
            src={sample.image_proxy_url}
            alt={sample.id}
            draggable={false}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: "0 0",
              maxWidth: "none",
              display: "block",
              pointerEvents: "none",
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9ca3af",
              fontSize: 13,
            }}
          >
            이미지 없음
          </div>
        )}
      </div>
    </div>
  );
}
